import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

const Rooms = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ room_name: "Conference Room A", title: "", start_at: "", end_at: "" });

  const load = async () => {
    try { const { data } = await api.get("/rooms/bookings"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const { data } = await api.post("/rooms/bookings", form);
      setList((l) => [data, ...l]); setOpen(false); toast.success("Booked");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="rooms-page">
      <PageHeader
        overline="Facilities"
        title="Meeting Rooms"
        description="Reserve conference rooms for staff meetings, parent conferences and academic reviews."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="book-room-btn"><Plus className="w-4 h-4 mr-1.5" /> Book Room</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Book a Meeting Room</DialogTitle></DialogHeader>
              <div className="grid gap-4 mt-2">
                <Fld label="Room"><Input value={form.room_name} onChange={(e) => setForm({ ...form, room_name: e.target.value })} /></Fld>
                <Fld label="Title / Purpose"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="room-title" /></Fld>
                <div className="grid grid-cols-2 gap-4">
                  <Fld label="Start"><Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} /></Fld>
                  <Fld label="End"><Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} /></Fld>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="room-submit">Confirm</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {list.length === 0 ? <EmptyState message="No bookings yet." /> : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map(b => (
            <Card key={b.id} className="p-5 border-[#E2E8F0]" data-testid={`booking-${b.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#1B2C5A]/5 text-[#1B2C5A] grid place-items-center"><Building2 className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-display text-lg text-[#1B2C5A]">{b.title}</h3>
                  <div className="text-sm text-[#475569]">{b.room_name}</div>
                  <div className="text-xs text-[#475569] mt-2">{new Date(b.start_at).toLocaleString()} → {new Date(b.end_at).toLocaleString()}</div>
                  <div className="text-xs text-[#475569] mt-1">Booked by {b.booked_by_name}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Rooms;
